import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import authService, { User, LoginCredentials } from '../services/auth.service';
import { apiService } from '../services/api.service';
import { useInaktivitaetsAbmeldung } from '../hooks/useInaktivitaetsAbmeldung';
import { InaktivitaetsWarnung } from '../components/auth/InaktivitaetsWarnung';
import {
  leseZeit, SCHLUESSEL_AKTIVITAET, SCHLUESSEL_GRUND, speicherLesen, speicherSchreiben, zustandBei,
} from '../utils/inaktivitaet';

/** Grund für die Anmeldeseite merken (nur dieser Tab). */
function merkeGrund(grund: string) {
  try { window.sessionStorage.setItem(SCHLUESSEL_GRUND, grund); } catch { /* nur Hinweis */ }
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  // Setup axios interceptor for automatic token refresh
  useEffect(() => {
    authService.setupAxiosInterceptor(() => {
      // On auth failure, clear user and force re-login
      setUser(null);
    });
  }, []);

  /**
   * Check if user is authenticated (by checking cookie)
   */
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      // Browser nach mehr als 5 Minuten ohne Nutzung wieder geöffnet:
      // abmelden, bevor Daten geladen werden.
      const letzte = leseZeit(speicherLesen(SCHLUESSEL_AKTIVITAET));
      if (letzte !== null && zustandBei(letzte, Date.now()) === 'abmelden') {
        await authService.logout();
        apiService.clearCsrfToken();
        merkeGrund('inaktiv');
        setUser(null);
        return;
      }
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      // Fetch CSRF token if user is authenticated
      if (currentUser) {
        await apiService.fetchCsrfToken();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Login with credentials
   * Backend will set httpOnly cookies on success
   */
  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await authService.login(credentials);
      if (response.success && response.user) {
        // Neue Sitzung: Inaktivität ab jetzt zählen
        speicherSchreiben(SCHLUESSEL_AKTIVITAET, String(Date.now()));
        setUser(response.user);
        // Fetch CSRF token after successful login
        await apiService.fetchCsrfToken();
        // Refresh user data from /auth/me to ensure all profile fields are loaded
        const fullUser = await authService.getCurrentUser();
        if (fullUser) {
          setUser(fullUser);
        }
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    }
  };

  /**
   * Logout and clear cookies
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      // Clear CSRF token on logout
      apiService.clearCsrfToken();
    } catch (error) {
      console.error('Logout error:', error);
      // Clear user even if logout request fails
      setUser(null);
      apiService.clearCsrfToken();
    }
  };

  /**
   * Refresh user data from server
   */
  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Refresh user error:', error);
      setUser(null);
    }
  };

  // Nach 5 Minuten ohne Nutzung abmelden; volle Neuladung leert alle
  // zwischengespeicherten Daten im Browser.
  const abmeldenWegenInaktivitaet = useCallback(async () => {
    merkeGrund('inaktiv');
    try { await authService.logout(); } catch { /* Cookies ggf. schon weg */ }
    apiService.clearCsrfToken();
    setUser(null);
    window.location.replace('/login');
  }, []);
  const { rest, weiter } = useInaktivitaetsAbmeldung(!!user, abmeldenWegenInaktivitaet);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        logout,
        isAuthenticated: !!user,
        refreshUser,
      }}
    >
      {children}
      {user && rest !== null && (
        <InaktivitaetsWarnung rest={rest} onWeiter={weiter} onAbmelden={abmeldenWegenInaktivitaet} />
      )}
    </AuthContext.Provider>
  );
};